/**
 * Responsibility: say what is on screen, in one line of badges.
 *
 * Replaces a sentence that read "50 of 805 shown · screened from 110,290
 * cards · built 2026-09-14 · click a row for detail". Every clause of that
 * was a fact worth having and none of them was findable at a glance, which
 * is what a run-on caption does to four separate numbers.
 *
 * The first badge is the algorithm, because it is the thing that decides
 * what the ranking below even means. There is one today; naming it now
 * means adding a second is a new value rather than a new concept the page
 * has to learn.
 *
 * The universe badge is there to be honest about scope: this board screens
 * a PSA population report that covers Pokemon, so a sports card will never
 * appear on it however the filters are set.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import AlgorithmIcon from '@mui/icons-material/AutoAwesomeMotion';
import type { CardEra, CardLanguage } from '../../api/client';
import type { DiscoveryFilters } from './discoveryFilters';
import { DEFAULT_FILTERS } from './discoveryFilters';

const LANGUAGE_LABEL: Record<CardLanguage, string> = {
  EN: 'English',
  JP: 'Japanese',
  ANY: 'All languages',
};

const ERA_LABEL: Record<CardEra, string> = {
  MODERN: 'Modern',
  VINTAGE: 'Vintage',
};

type DiscoveryBadgesProps = {
  filters: DiscoveryFilters;
  onChange: (next: DiscoveryFilters) => void;
  /** Rows on screen, after the keyword filter. */
  shown: number;
  /** Rows the screen produced before the keyword filter. */
  matched: number;
  /** Everything the screen considered. */
  candidates: number;
  universeSize: number;
  universeBuilt: string | null;
};

export function DiscoveryBadges({
  filters,
  onChange,
  shown,
  matched,
  candidates,
  universeSize,
  universeBuilt,
}: DiscoveryBadgesProps) {
  const clear = <K extends keyof DiscoveryFilters>(key: K) =>
    onChange({ ...filters, [key]: DEFAULT_FILTERS[key] });

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
      <Chip
        size="small"
        color="primary"
        icon={<AlgorithmIcon sx={{ fontSize: 15 }} />}
        label="Arbitrage"
      />

      <Chip
        size="small"
        variant="outlined"
        label={
          matched !== candidates
            ? `${shown}/${matched} of ${candidates}`
            : `${shown}/${candidates}`
        }
      />

      <Chip
        size="small"
        variant="outlined"
        label={
          universeBuilt
            ? `${universeSize.toLocaleString()} Pokemon cards, built ${universeBuilt}`
            : `${universeSize.toLocaleString()} Pokemon cards screened`
        }
      />

      {/* The filters themselves, so one is never on without being seen.
          Each is removable where removing it means anything - era and
          language always have a value, so those are shown and not
          dismissable. */}
      <Chip size="small" variant="outlined" label={ERA_LABEL[filters.era]} />
      <Chip size="small" variant="outlined" label={LANGUAGE_LABEL[filters.language]} />
      <Chip size="small" variant="outlined" label={`pop ${filters.floor}+`} />

      {filters.keywords.trim() ? (
        <Chip
          size="small"
          color="secondary"
          label={`"${filters.keywords.trim()}"`}
          onDelete={() => clear('keywords')}
        />
      ) : null}
    </Stack>
  );
}
